// Estado da UI em signals. Fala com dados e busca só pelas interfaces públicas (CardStore, SearchClient).
import { batch, computed, effect, type ReadonlySignal, signal } from "@preact/signals";
import type { Card, Tag } from "../../../shared/types.ts";
import { createStore, type Notice } from "../data/index.ts";
import { createSearch, type SearchResult, serverQuery } from "../search/index.ts";
import { DENSITY, type Density, expireUnlessHeld, KIND_LABEL, quietIngest, syncIndex } from "./lib.ts";

export const store = createStore();
const searchClient = createSearch();

export type Me = { id: string; email: string };
/** undefined = carregando; null = sem sessão. */
export const me = signal<Me | null | undefined>(undefined);

// ---------- Análise automática (chave da Anthropic no servidor) ----------
/** A análise automática está ligada no servidor (aiEnabled de /v1/me/usage). null: ainda não se sabe. */
export const aiEnabled = signal<boolean | null>(null);
let askedAi = false;
/**
 * Card guardado sem a chave da IA (partial/ai_unavailable, quietIngest) que fica calmo no detalhe: sem "Parcial", sem
 * "Analisar de novo" e com a linha de que o resumo chega depois. Com a chave ligada desde então, o mesmo card oferece a
 * análise. Pergunta ao servidor uma vez, sem aviso (sem rede, continua calmo e pergunta de novo depois).
 */
export function waitsForAi(c: Pick<Card, "ingestState" | "ingestError">): boolean {
  if (!quietIngest(c)) return false;
  if (!askedAi && aiEnabled.peek() === null) {
    askedAi = true;
    fetch("/v1/me/usage", { credentials: "include" })
      .then((r) => (r.ok ? (r.json() as Promise<{ aiEnabled?: unknown }>) : null))
      .then((u) => void (typeof u?.aiEnabled === "boolean" && (aiEnabled.value = u.aiEnabled)), () => void (askedAi = false));
  }
  return aiEnabled.value !== true;
}

// ---------- localStorage (pode falhar: aba privada, site bloqueado) ----------
export function load(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
export function save(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {}
}

// ---------- Rotas: "/" (Tudo), "/lixeira", "/colecoes" e "/reencontro" (em breve); detalhe no hash (#<cardId>) ----------
const readRoute = () => ({ path: location.pathname, cardId: decodeURIComponent(location.hash.slice(1)) || null });
export const route = signal(readRoute());
addEventListener("popstate", () => (route.value = readRoute()));
// Card aberto por um link #id da própria página (entrada nova no histórico): a entrada fica marcada e fechar volta
// nela (closeCard), então o gesto de voltar do iOS e o botão do Android fecham o detalhe sem deixar entrada morta.
// Endereço aberto de fora com #id não é marcado: voltar dali sairia do app.
addEventListener("hashchange", () => {
  if (location.hash && history.state === null) history.replaceState({ detail: 1 }, "", location.href);
  route.value = readRoute();
});
// Cada entrada empilhada por go() guarda a página de onde veio (from): o voltar da página (up) usa isso.
export function go(path: string) {
  if (location.pathname !== path) history.pushState({ from: location.pathname }, "", path);
  route.value = readRoute();
}
/**
 * Voltar da página para `parent` (seta da barra, "Voltar para…"). Veio de lá: volta no histórico, então o gesto do iOS e
 * o Voltar do Android depois dele não devolvem a página que a pessoa deixou. Abriu direto ou veio de outra página: troca
 * esta entrada pela do pai, sem empilhar.
 */
export function up(parent: string) {
  if (history.state?.from === parent) return history.back();
  history.replaceState(history.state, "", parent);
  route.value = readRoute();
}
/** onClick do voltar da página; com modificador (abrir em outra aba) fica com o navegador. */
export const backTo = (parent: string) => (e: MouseEvent) => {
  if (e.button || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  up(parent);
};
/** Troca o card aberto sem empilhar histórico (setas, ligações) ou tira o #id da entrada (null). Não mexe na rolagem. */
export function showCard(id: string | null) {
  const from = history.state?.from;
  history.replaceState(id ? history.state : from ? { from } : null, "", location.pathname + location.search + (id ? `#${encodeURIComponent(id)}` : ""));
  route.value = readRoute();
}
/**
 * Fecha o detalhe (×, Esc, fundo, apagar, buscar uma etiqueta). Entrada marcada: volta no histórico, como o gesto de
 * voltar; `then` roda depois da volta (um go() antes dela empilharia na entrada errada).
 */
export function closeCard(then?: () => void) {
  if (!history.state?.detail || !route.peek().cardId) {
    showCard(null);
    return then?.();
  }
  if (then) addEventListener("popstate", () => then(), { once: true });
  history.back();
}

// ---------- Busca ----------
export const chips = signal<string[]>([]);
export const text = signal("");
export const query = computed(() => [...chips.value, text.value.trim()].filter(Boolean).join(" "));
const result = signal<SearchResult | null>(null);
/** Último resultado local (também da busca vazia): facetas, cores, idiomas e a consulta interpretada (dicas). */
export const searchResult: ReadonlySignal<SearchResult | null> = result;
/** Ids que o servidor achou por significado (§7.4), já cruzados com os filtros locais; entram depois dos achados locais. */
const meaningIds = signal<string[]>([]);
/** Busca por significado: "loading" (esperando o servidor), "offline" (sem rede: fica só a busca local) ou null. */
export const meaning = signal<"loading" | "offline" | null>(null);
/** A grade mostra (ou espera) cards que só o servidor achou: uma coleção automática, avaliada aqui, não os teria (U-21). */
export const fromServer = computed(() => meaning.value === "loading" || meaningIds.value.length > 0);
const indexVersion = signal(0);
/** Sobe a cada mudança que chega ao índice (cards, etiquetas, coleções): coleção automática reavalia com ele. */
export const indexTick: ReadonlySignal<number> = indexVersion;

export const byId = computed(() => new Map([...store.cards.value, ...store.trash.value].map((c) => [c.id, c])));
/** Lista mostrada na grade (e usada pelas setas do detalhe). */
export const visible = computed<Card[]>(() => {
  if (!query.value) return store.cards.value.filter((c) => !c.isPrivate); // privado só aparece buscando #privado (§4.2)
  const map = byId.value;
  const ids = result.value?.ids ?? [];
  const local = new Set(ids);
  return [...ids, ...meaningIds.value.filter((id) => !local.has(id))].map((id) => map.get(id)).filter((c): c is Card => !!c);
});
/** Tipos no conjunto pesquisado (o acervo, com a busca vazia), do mais frequente ao menos. Sem privados (§4.2). */
export const kindCounts = computed(() => Object.entries(result.value?.facets ?? {}).sort((a, b) => b[1] - a[1]) as [Card["kind"], number][]);
/** Etiquetas mais usadas (sugestões no detalhe). */
export const topTags = computed(() => {
  const n = new Map<string, number>();
  for (const c of store.cards.value) for (const t of c.tagIds) n.set(t, (n.get(t) ?? 0) + 1);
  const tags = store.tags.value;
  return [...n].sort((a, b) => b[1] - a[1]).map(([id]) => tags.get(id)).filter((t): t is Tag => !!t);
});
export const facetToken = (kind: Card["kind"]) => `tipo:${KIND_LABEL[kind]}`;
export function searchFor(token: string) {
  chips.value = [token];
  text.value = "";
  go("/");
}

syncIndex(
  store.cards,
  store.tags,
  searchClient,
  () => void indexVersion.value++,
  () => toast({ key: "index", text: "A busca não conseguiu se atualizar. Recarregue a página." }),
);

// Coleções no índice: em: na busca e as coleções automáticas (smartSpace).
effect(() => {
  const rows = [...store.spaceCards.value.values()].flat();
  searchClient.setSpaces(store.spaces.value, rows).then(() => void indexVersion.value++, () => {});
});
export const smartSpace = (spaceId: string) => searchClient.smartSpace(spaceId);

// Cada tecla só busca (o índice não é recarregado); a consulta "lixeira" procura em store.trash. Vale a resposta mais recente.
// A busca vazia também roda: dá as facetas do acervo para a linha de chips.
let seq = 0;
effect(() => {
  const q = query.value;
  void indexVersion.value;
  const trash = store.trash.value;
  const mine = ++seq;
  searchClient
    .search(q, { trash })
    .then((r) => {
      if (mine !== seq) return;
      result.value = r;
      askServer(r);
    })
    .catch(() => {});
});

// ---------- Busca por significado (§7.4): com "~", ou quando o texto não achou nada aqui, e só com rede ----------
const ASK_DELAY = 350; // ms parado de digitar antes de perguntar (o servidor limita 30 por minuto)
// ponytail: respostas guardadas pela sessão (30 consultas); um card novo só entra na parte semântica ao recarregar.
const answers = new Map<string, string[]>();
/** Pergunta no ar por consulta: o índice que muda no meio dela (um sync) espera a mesma resposta em vez de perguntar de novo. */
const asking = new Map<string, Promise<string[]>>();
let askTimer: ReturnType<typeof setTimeout> | undefined;
let latest = 0; // a resposta vale para a última chamada (a da consulta que está na tela)
function askServer(r: SearchResult) {
  clearTimeout(askTimer);
  const mine = ++latest;
  // filterIds vazio = nenhum card passa nos filtros (tipo desconhecido, etiqueta sem cards): nada a perguntar.
  const s = r.filterIds?.length ? serverQuery(r.query) : null;
  const allowed = new Set(r.filterIds);
  const show = (ids: string[]) =>
    batch(() => {
      meaningIds.value = ids.filter((id) => allowed.has(id)); // privado, lixeira e filtros locais (data, cor, em:…) valem
      meaning.value = null;
    });
  if (!s) return show([]);
  const known = answers.get(s);
  if (known) return show(known);
  const online = store.status.peek().online && (typeof navigator === "undefined" || navigator.onLine !== false);
  batch(() => {
    meaningIds.value = [];
    meaning.value = online ? "loading" : "offline";
  });
  if (!online) return;
  const ask = () => {
    let p = asking.get(s);
    if (!p) {
      p = store
        .serverSearch(s)
        .then((res) => {
          answers.delete(s);
          answers.set(s, res.ids);
          if (answers.size > 30) answers.delete(answers.keys().next().value!);
          return res.ids;
        })
        .finally(() => asking.delete(s));
      asking.set(s, p);
    }
    p.then(
      (ids) => mine === latest && show(ids),
      () => mine === latest && (meaning.value = null), // o store já avisou (429, sessão); fica a busca local
    );
  };
  if (asking.has(s)) ask();
  else askTimer = setTimeout(ask, ASK_DELAY);
}

// A rede voltou com o aviso "sem conexão" na tela: pergunta agora, sem esperar outra tecla.
effect(() => {
  const r = result.peek();
  if (store.status.value.online && meaning.peek() === "offline" && r) askServer(r);
});

// ---------- Avisos (toasts) ----------
export type Toast = { key: string; text: string; action?: { label: string; run: () => void } };
export const toasts = signal<Toast[]>([]);
export function toast(t: Toast, ms = 6000) {
  const now = toasts.peek(); // peek: chamado dentro de effects, não pode criar dependência
  if (now.some((x) => x.key === t.key)) return;
  toasts.value = [...now, t];
  // Com foco ou mouse em cima o aviso fica: sumir com o botão focado jogaria o foco no body.
  const held = () => [...document.querySelectorAll<HTMLElement>(".toast")].some((el) => el.dataset.key === t.key && el.matches(":hover, :focus-within"));
  expireUnlessHeld(held, () => dismiss(t.key), ms);
}
export const dismiss = (key: string) => (toasts.value = toasts.peek().filter((t) => t.key !== key));
/** Depois do próximo render: se o elemento com foco sumiu (foco caiu no body), leva o foco para `to()`. */
export function refocus(to: () => HTMLElement | null | undefined, opts?: FocusOptions) {
  requestAnimationFrame(() => {
    const now = document.activeElement;
    if (!now || now === document.body) to()?.focus(opts);
  });
}
/** Diálogo modal aberto. Fora dele tudo fica inerte (nem clique nem leitor de tela), então os avisos são desenhados dentro dele. */
export const modal = signal<HTMLDialogElement | null>(null);
export function showModal(d: HTMLDialogElement) {
  d.showModal();
  modal.value = d;
  d.addEventListener("close", () => modal.value === d && (modal.value = null), { once: true });
}

const undo = (cardId: string): Toast => ({
  key: `del:${cardId}`,
  text: "Card movido para a lixeira.",
  action: { label: "Desfazer", run: () => void store.restore(cardId) },
});
function noticeToast(n: Notice): [Toast, number] {
  switch (n.kind) {
    case "deduped":
      return [{ key: n.id, text: "Isso já estava guardado. Subiu para o topo." }, 6000];
    case "conflict":
      return [{ key: n.id, text: "Este card mudou em outro aparelho ao mesmo tempo. Sua versão virou uma cópia; nada se perdeu.", action: { label: "Ver cópia", run: () => showCard(n.copyId) } }, 10000];
    case "deleted":
      return [undo(n.cardId), 5000];
    case "error":
      return [{ key: n.id, text: n.message }, 8000];
  }
}
// Outra aba saiu da conta (ou abriu um banco mais novo) e esta largou a réplica: recarregar pega a sessão e o app atuais.
effect(() => void (store.status.value.error === "signed_out" && location.reload()));

effect(() => {
  const ns = store.notices.value;
  for (const n of ns) toast(...noticeToast(n));
  for (const n of ns) store.dismissNotice(n.id);
});

export async function deleteCard(id: string) {
  await store.remove(id);
  toast(undo(id), 5000);
}
/** Erro de escrita local vira aviso; nunca some em silêncio. Erro que o store já avisou (cause "notified") não repete. */
export const failed = (err?: unknown) =>
  (err as Error | undefined)?.cause !== "notified" && toast({ key: `fail:${Date.now()}`, text: "Não deu para salvar. Tente de novo." });

// ---------- Tema ----------
export type Theme = "light" | "dark" | "system";
const stored = load("faig.theme");
export const theme = signal<Theme>(stored === "light" || stored === "dark" ? stored : "system");
effect(() => {
  const t = theme.value;
  if (t === "system") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = t;
  save("faig.theme", t === "system" ? null : t);
});

// ---------- Densidade da grade (faig.density) ----------
const storedDensity = load("faig.density");
export const density = signal<Density>(storedDensity && Object.hasOwn(DENSITY, storedDensity) ? (storedDensity as Density) : "amplo");
effect(() => {
  document.documentElement.dataset.density = density.value;
  save("faig.density", density.value === "amplo" ? null : density.value);
});

// ---------- Largura (colunas da grade; mesmas faixas das media queries) ----------
export const width = signal(innerWidth);
addEventListener("resize", () => (width.value = innerWidth));
/** Largura da barra de rolagem clássica (0 com barra sobreposta). */
export const scrollbar = () => innerWidth - document.documentElement.clientWidth;

// ---------- Rolagem entre telas ----------
// Cada tela abre no topo; Tudo (sem busca) volta onde estava. O navegador não restaura sozinho: no app instalado não há
// botão de voltar, e as abas trocam de tela sem recarregar. O efeito roda quando a rota muda, antes do render: a grade
// de Tudo ainda está na tela. Guarda o card no alto da tela (não o scrollY): a grade remontada abre até ele, por mais
// fundo que esteja (Grid.tsx), e ele volta ao mesmo ponto mesmo se outro card entrou no topo enquanto isso.
if ("scrollRestoration" in history) history.scrollRestoration = "manual";
/** Card de Tudo que estava no alto da tela quando a pessoa saiu, e a distância dele até o topo da janela. */
export let resumeAt: { id: string; top: number } | null = null;
const cardLink = (id: string) => `.grid a.card-open[href="#${CSS.escape(encodeURIComponent(id))}"]`;
let shownPath = route.peek().path;
effect(() => {
  const path = route.value.path;
  if (path === shownPath) return;
  if (shownPath === "/" && !query.peek()) {
    // O primeiro card (da primeira coluna) que aparece na tela.
    const a = [...document.querySelectorAll<HTMLAnchorElement>(".grid a.card-open")].find((el) => el.getBoundingClientRect().bottom > 0);
    resumeAt = a ? { id: decodeURIComponent(a.hash.slice(1)), top: a.getBoundingClientRect().top } : null;
  }
  shownPath = path;
  const back = path === "/" && !query.peek() ? resumeAt : null;
  requestAnimationFrame(() => {
    const el = back && document.querySelector(cardLink(back.id));
    scrollTo(0, el ? scrollY + el.getBoundingClientRect().top - back.top : 0);
  });
});
// Busca nova (outro termo, não vazia) começa do primeiro resultado: a barra fica presa no topo do celular e dá para
// digitar com a grade rolada.
let lastQuery = query.peek();
effect(() => {
  const q = query.value;
  if (q && q !== lastQuery && scrollY > 0) scrollTo(0, 0);
  lastQuery = q;
});

// ---------- Teclado na tela ----------
// O único lugar que mede o teclado (o shell, a nota rápida, /capturado, o detalhe e o lote leem daqui). Nem o iOS nem o
// Chrome do Android encolhem a janela de layout quando o teclado abre, só a área visível (visualViewport): o que é fixo
// embaixo ficaria atrás dele. --kb é a distância do pé da área visível até o pé da janela, --vvt quanto a área visível
// desceu e data-kb marca teclado aberto (acima de 80 px: barra de sugestões e a régua do zoom não contam). Pinça (zoom)
// encolhe a área visível sem teclado. Este módulo carrega antes dos que também ouvem o visualViewport (RichText): eles
// já leem o --kb novo.
const vv = typeof visualViewport === "undefined" ? null : visualViewport;
if (vv) {
  const root = document.documentElement;
  const keyboard = () => {
    const zoomed = vv.scale > 1.01;
    const kb = zoomed ? 0 : Math.max(0, Math.round(innerHeight - vv.height - vv.offsetTop));
    root.style.setProperty("--kb", `${kb}px`);
    root.style.setProperty("--vvt", `${zoomed ? 0 : Math.round(vv.offsetTop)}px`);
    root.toggleAttribute("data-kb", kb > 80);
  };
  vv.addEventListener("resize", keyboard);
  vv.addEventListener("scroll", keyboard);
  keyboard();
}
